import React from 'react';
import classNames from 'classnames';

import Typography from '../../Typography/index.js';
import { generateRandomId } from '../../../utils/random.js';

type TextFieldSize = 'small' | 'medium' | 'large';

export interface TextFieldProps
  extends Omit<
    React.DetailedHTMLProps<
      React.InputHTMLAttributes<HTMLInputElement>,
      HTMLInputElement
    >,
    'type' | 'size'
  > {
  /**
   * An optional error message to show to the user.
   */
  error?: string;

  /**
   * An optional label to pass to the input element.
   */
  label?: string;

  /**
   * The size of the text and input field.
   * @default "large"
   */
  size?: TextFieldSize;
}

/**
 * A form field that a user can use to add information by typing. They have a
 * range of options, including different states & limitations
 * (like character counts or only accepting numbers).
 *
 * Labels are always optional, and if you need to remove one just delete it
 * from the instance, and it will auto adjust.
 *
 * Default inputs are for data entry. The user can see a placeholder text if
 * necessary, and can include a label. When there’s a label present, the error
 * state should be displayed inline with the label, and below when there is no
 * label. All inputs in the patient app should inherit the label prop, and can
 * be removed in admin interfaces.
 * A disabled input should use cursor: not-allowed
 *
 * Use small sizing for admin & staff apps, or anywhere you need to use this
 * input on a desktop format outside of a patient facing product. Label size
 * shrinks, and error state is positioned inline with the label. There is no
 * helper option.
 *
 * @see https://www.figma.com/file/LdhxwbVqKRRLpyK5ekr4Q8/Component-Library?node-id=1850%3A1239
 */
const TextField = React.forwardRef(
  (
    { className, error, label, size = 'large', ...rest }: TextFieldProps,
    ref: React.ForwardedRef<HTMLInputElement>,
  ) => {
    const hasError = Boolean(error);

    const errorId = generateRandomId();

    const Error = error ? (
      <Typography
        as="span"
        color="danger"
        variant={size === 'large' ? 'paragraph-medium' : 'paragraph-small'}
        id={errorId}
      >
        {error}
      </Typography>
    ) : null;

    const Input = (
      <input
        aria-invalid={hasError}
        aria-errormessage={hasError ? errorId : undefined}
        className={classNames(
          'text-field',
          `text-field--${size}`,
          { error: hasError },
          className,
        )}
        {...rest}
        ref={ref}
      />
    );

    if (label) {
      const titleVariant =
        size === 'large'
          ? 'title-large'
          : size === 'medium'
            ? 'title-medium'
            : 'title-small';

      return (
        <div className="text-field-container">
          <Typography
            as="label"
            variant={titleVariant}
            bold
            className={'text-field-label'}
          >
            {label}
            {Input}
          </Typography>
          {Error}
        </div>
      );
    }

    return (
      <div className="text-field-container">
        {Input}
        {Error}
      </div>
    );
  },
);

TextField.displayName = 'TextField';

export default TextField;
